/**
 * ステム再生の方式を決めるロジック（副作用なし）
 *
 * ボーカル／インストを別々の音量で鳴らすには 2 音源をミックスする必要があるが、
 * Web Audio でミックスすると iOS のバックグラウンド再生が効かなくなる。
 * 音量が振り切っている組み合わせは分離済みファイル 1 本で同じ音になるため、
 * そのときは従来どおり audio 要素で鳴らしてバックグラウンド再生を維持する。
 */

export type StemVariant = 'vocals' | 'no_vocals'

export type StemPlan =
  /** audio 要素で単一ファイルを鳴らす。stem が null なら原曲 */
  | { engine: 'element'; stem: StemVariant | null }
  /** Web Audio で 2 音源をサンプル単位で同期させて鳴らす */
  | { engine: 'mix'; vocalVolume: number; instVolume: number }

export type StemPlanInput = {
  /** ステム機能そのものの ON/OFF */
  enabled: boolean
  vocalVolume: number
  instVolume: number
  /** この曲に分離済みファイルがあるか。未判定の間は false 扱いにする */
  stemsAvailable: boolean
}

const ORIGINAL: StemPlan = { engine: 'element', stem: null }

const clampVolume = (value: number): number => {
  if (!Number.isFinite(value)) return 1
  return Math.min(Math.max(value, 0), 1)
}

export const resolveStemPlan = ({ enabled, vocalVolume, instVolume, stemsAvailable }: StemPlanInput): StemPlan => {
  if (!enabled || !stemsAvailable) return ORIGINAL

  const vocal = clampVolume(vocalVolume)
  const inst = clampVolume(instVolume)

  if (vocal >= 1 && inst >= 1) return ORIGINAL
  if (vocal <= 0 && inst >= 1) return { engine: 'element', stem: 'no_vocals' }
  if (vocal >= 1 && inst <= 0) return { engine: 'element', stem: 'vocals' }

  return { engine: 'mix', vocalVolume: vocal, instVolume: inst }
}

/** 2 つのプランが同じ鳴り方になるか（音量差は gain で吸収できるので engine と stem だけ見る） */
export const isSameEngineTarget = (a: StemPlan, b: StemPlan): boolean => {
  if (a.engine !== b.engine) return false
  if (a.engine === 'element' && b.engine === 'element') return a.stem === b.stem
  return true
}

/** このプランでバックグラウンド再生が維持できるか */
export const keepsBackgroundPlayback = (plan: StemPlan): boolean => plan.engine === 'element'
